import React from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import Hyperlink from '../../common/Hyperlink';
import styled from 'styled-components';
import { media } from '../../../styles/style-utils';
import { legacyType } from '../../../styles/typography';

import Section from '../../common/Section';
import Anchor from '../utils/Anchor';
import PlanDetails from './PlanDetails';
import { withRouteData, withSiteData } from 'react-static';
import {
  GIFT_CARD_PATH,
  PLAN_ID_PPC_12M,
  PLAN_ID_PP_12M,
  SUBSCRIPTION_PLANS,
} from '../../../constants';
import premiumPlusBg from '../../../assets/images/plans/plan-pp.svg';
import premiumConcertsBg from '../../../assets/images/plans/plan-ppc.svg';
import SpecialOffer from './SpecialOffer';

const PlansSectionContainer = styled.div`
  text-align: left;
  position: relative;
  font-size: var(--fs-s);
  padding: 0 var(--default-horizontal-padding);
  max-width: 2048px;
  left: 50%;
  transform: translateX(-50%);

  .header {
    margin-top: calc(var(--fr) * 2);
    margin-bottom: calc(var(--fr) * 3);
    text-align: center;

    h3 {
      font-weight: 400;
    }
  }

  ${media.large`
    padding: 0 calc(3.5 * var(--fr));
  `}

  ${media.extraLarge`
    padding: 0 var(--default-horizontal-padding);
  `}
`;

const PlansTable = styled.div`
  display: grid;
  grid-template-areas:
    'plan-1'
    'plan-2'
    'student'
    'gift-card'
    'free';
  gap: calc(1.5 * var(--fr));

  ${media.large`
    grid-template-areas:
      'plan-1 plan-2'
      'student gift-card'
      'free free';
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}

  .plan-1 {
    grid-area: plan-1;
    border: 1px solid var(--c-white);
  }

  .plan-2 {
    grid-area: plan-2;
    border: 1px solid var(--c-shark);
  }

  .student {
    grid-area: student;
  }

  .gift-card {
    grid-area: gift-card;
  }

  .free {
    grid-area: free;
  }
`;

const BestValueTag = styled.div`
  ${legacyType('xs', 'label', { uppercase: true, lineHeight: false })};
  text-transform: uppercase;
  background-color: var(--c-white);
  padding: calc(0.25 * var(--fr)) calc(1 * var(--fr));
  font-size: var(--fs-base);
  line-height: 1;
  border-radius: var(--fr);
  font-weight: 500;
  color: var(--c-shark);
  width: fit-content;
`;

const FooterNote = styled.div`
  display: grid;
  font-size: var(--fs-base);
  color: var(--c-manatee);
  margin-top: calc(1.25 * var(--fr));
  text-align: center;

  a {
    text-decoration: underline;
  }
`;

const messages = defineMessages({
  premiumPlusTitle: {
    id: 'index.plans-section.premium-plus.plan-title',
    defaultMessage: 'Premium Audio',
  },
  premiumConcertsTitle: {
    id: 'index.plans-section.premium-concerts.plan-title',
    defaultMessage: 'Premium Audio + Video',
  },
  cta: {
    id: 'index.plans-section.start-free-trial',
    defaultMessage: 'Start free trial',
  },
});

export const objectivesMessages = defineMessages({
  adFree: {
    id: 'index.plans-overview.p.ad-free',
    defaultMessage: 'On-demand and ad-free listening',
  },
  collection: {
    id: 'index.plans-overview.p.collection',
    defaultMessage: 'Unlimited personal collection of 2+ million tracks and exclusive releases',
  },
  weeklyMixes: {
    id: 'index.plans-overview.p.weekly-mixes',
    defaultMessage: 'Curated recommendations & weekly mixes',
  },
  quality: {
    id: 'index.plans-overview.pp.lossless',
    defaultMessage: 'Lossless audio quality (FLAC 16bit 44.1kHz)',
  },
  connect: {
    id: 'index.plans-overview.pp.connect',
    defaultMessage: 'Connect to Sonos and more',
  },
  concertAccess: {
    id: 'index.plans-overview.ppc.access',
    defaultMessage: 'Access all IDAGIO Concerts',
  },
});

class PlansSection extends React.Component {
  onClick = title => {
    if (typeof document !== 'undefined' && window.analytics) {
      analytics.track('Clicked CTA Button', {
        title,
        from: 'Plans Section',
      });
    }
  };

  render() {
    return (
      <Section
        sectionMargin="2rem"
        textAlign="center"
        padding="var(--default-vertical-padding) 0 0 0"
      >
        <Anchor id="plans-section" />
        <PlansSectionContainer>
          <header className="header">
            <h2>{this.props.content.title}</h2>
            <h3>{this.props.content.subtitle}</h3>
          </header>
          <PlansTable>
            <PlanDetails
              imageTitle={<FormattedMessage {...messages.premiumPlusTitle} />}
              price={SUBSCRIPTION_PLANS[PLAN_ID_PP_12M].monthlyPrice}
              imageUrl={premiumPlusBg}
              interval="monthly"
              benefits={[
                {
                  key: 'adFree',
                  value: <FormattedMessage {...objectivesMessages.adFree} />,
                },
                {
                  key: 'collection',
                  value: <FormattedMessage {...objectivesMessages.collection} />,
                },
                {
                  key: 'weeklyMixes',
                  value: <FormattedMessage {...objectivesMessages.weeklyMixes} />,
                },
                {
                  key: 'connect',
                  value: <FormattedMessage {...objectivesMessages.connect} />,
                },
                {
                  key: 'quality',
                  value: <FormattedMessage {...objectivesMessages.quality} />,
                },
              ]}
              exclusions={[
                {
                  key: 'concertAccess',
                  value: <FormattedMessage {...objectivesMessages.concertAccess} />,
                },
              ]}
              planTag={
                <BestValueTag>
                  <FormattedMessage
                    id="index.plans-section.premium-plus.most-popular"
                    defaultMessage="Most Popular"
                  />
                </BestValueTag>
              }
              link={`${this.props.appDomain}/discover?fromId=lpPlans&showSubscribeModal=true&plan=${PLAN_ID_PP_12M}`}
              trackingTitle="Premium"
              ctaButtonLabel={<FormattedMessage {...messages.cta} />}
              className="plan-1"
              onClick={this.onClick}
            />
            <PlanDetails
              imageTitle={<FormattedMessage {...messages.premiumConcertsTitle} />}
              price={SUBSCRIPTION_PLANS[PLAN_ID_PPC_12M].monthlyPrice}
              imageUrl={premiumConcertsBg}
              interval="monthly"
              benefits={[
                {
                  key: 'adFree',
                  value: <FormattedMessage {...objectivesMessages.adFree} />,
                },
                {
                  key: 'collection',
                  value: <FormattedMessage {...objectivesMessages.collection} />,
                },
                {
                  key: 'weeklyMixes',
                  value: <FormattedMessage {...objectivesMessages.weeklyMixes} />,
                },
                {
                  key: 'connect',
                  value: <FormattedMessage {...objectivesMessages.connect} />,
                },
                {
                  key: 'quality',
                  value: <FormattedMessage {...objectivesMessages.quality} />,
                },
                {
                  key: 'concertAccess',
                  value: <FormattedMessage {...objectivesMessages.concertAccess} />,
                },
              ]}
              link={`${this.props.appDomain}/discover?fromId=lpPlans&showSubscribeModal=true&plan=${PLAN_ID_PPC_12M}`}
              trackingTitle="Premium+ Concerts"
              ctaButtonLabel={<FormattedMessage {...messages.cta} />}
              className="plan-2"
              onClick={this.onClick}
            />
            <SpecialOffer
              to="/students"
              className="student"
              title={
                <FormattedMessage
                  id="index.plans-section.student.header"
                  defaultMessage="Save 50% as a Student"
                />
              }
              description={
                <FormattedMessage
                  id="index.plans-section.student.description"
                  defaultMessage="All plans at half price for students. Dive into classical music for the price of a Latte."
                />
              }
              trackingTitle="Student"
              onClick={this.onClick}
            />
            <SpecialOffer
              to={`/${GIFT_CARD_PATH}`}
              className="gift-card"
              title={
                <FormattedMessage
                  id="index.plans-section.gift-card-2.header"
                  defaultMessage="Gift a Subscription"
                />
              }
              description={
                <FormattedMessage
                  id="index.plans-section.gift-card-2.description"
                  defaultMessage="Share the joy of classical music with someone special."
                />
              }
              trackingTitle="Gift Card"
              onClick={this.onClick}
            />
            <SpecialOffer
              to={`${this.props.appDomain}/join`}
              className="free"
              title={
                <FormattedMessage
                  id="index.plans-section.free.header"
                  defaultMessage="Ease your way in"
                />
              }
              description={
                <FormattedMessage
                  id="index.plans-section.free.description"
                  defaultMessage="Create a free account and start listening before choosing a plan."
                />
              }
              openInNewTab
              trackingTitle="Free"
              onClick={this.onClick}
            />
          </PlansTable>
          <FooterNote>
            <FormattedMessage
              id="index.plan-details.billing-note"
              defaultMessage="*When billed annually. Monthly plan available."
            />
            <FormattedMessage
              id="index.plan-details.terms-privacy-apply"
              defaultMessage="{termsLink} and {privacyPolicyLink} apply."
              values={{
                termsLink: (
                  <Hyperlink to="/terms" openInNewTab>
                    <FormattedMessage
                      id="account.general-terms"
                      defaultMessage="Terms & Conditions"
                    />
                  </Hyperlink>
                ),
                privacyPolicyLink: (
                  <Hyperlink to="/privacy" openInNewTab>
                    <FormattedMessage id="account.privacy-policy" defaultMessage="Privacy Policy" />
                  </Hyperlink>
                ),
              }}
            />
          </FooterNote>
        </PlansSectionContainer>
      </Section>
    );
  }
}

export default withSiteData(withRouteData(injectIntl(PlansSection)));



// WEBPACK FOOTER //
// /tmp/build_83f21ab6/src/components/main/plans/PlansSection.js